/** @jsxImportSource @emotion/react */
import { PageWrapper } from "../../components/page-skeleton/PageWrapper";
import { CardContainer } from "../../components/page-skeleton/CardContainer";
import { PageHeader } from "../../components/page-skeleton/PageHeader";
import { css } from "@emotion/react";
import { Table } from "../../components/table-components/Table";
import { TableRowHeader } from "../../components/table-components/TableRowHeader";
import { TableCellHeaderText } from "../../components/table-components/TableCellHeaderText";
import { PageFooter } from "../../components/page-skeleton/PageFooter";
import { StyledText } from "../../assets/typography/typography";
import { TableRow } from "../../components/table-components/TableRow";
import { TableCellText } from "../../components/table-components/TableCellText";
import { colors } from "../../components/colors";
import {
  useGetMenuRecommendedProducts,
  useUpdateMenuRecommendedProducts,
} from "./service/menus-api";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import * as authSelectors from "../auth/authSelectors";
import { Avatar } from "../../components/Avatar";
import { formatMoneyAmount } from "../../util";
import { SpinnerBlock } from "../common/Spinner";
import { RecommendedProductDropdown } from "./components/RecommendedProductDropdown";
import { AddRecommendedProductsSelector } from "./AddRecommendedProductsSelector";
import { DragAndDropContainer } from "../../components/DragAndDropContainer";
import Sortable from "sortablejs";
import { MutableRefObject } from "react";
import { RecommendedProduct } from "./models/Menus";
import { toastr } from "react-redux-toastr";
import { getErrorMessage, useHandleFetchError } from "../../error-handler";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";

const dropdownWrapperStyle = css`
  display: flex;
  align-items: end;
  flex-direction: row;
  gap: 8px;
  justify-content: end;
`;

const ellipsisWrapperStyle = css`
  display: flex;
  flex-direction: row;
  justify-content: end;
  width: fit-content;
`;

const dragAndDropRowStyle = css`
  cursor: grab;
`;

const dragAndDropContainerStyle = css`
  border-bottom: 1px solid ${colors.greyBorder};
`;

const productNameContainerStyle = css`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
`;

export const MenuRecommendedProductsUpsert = () => {
  const { id } = useParams<{
    id: string;
  }>();
  const organizationKeyName = useSelector(
    authSelectors.selectedOrganizationKeyName
  );
  const {
    data,
    isLoading,
    isRefetching,
    error: getMenuRecommendedProductsError,
  } = useGetMenuRecommendedProducts(organizationKeyName, id);

  const {
    mutateAsync: updateRecommendedProducts,
  } = useUpdateMenuRecommendedProducts(id);

  const onDrop = async (
    event: Sortable.SortableEvent,
    containerRef: MutableRefObject<HTMLElement | null>
  ) => {
    if (!containerRef) {
      return;
    }
    // iterating over dom children. Each of them has the id of the product as data attribute
    // @ts-expect-error
    const products = [...containerRef.current?.children].map((b, index) => {
      // Id of the product
      const productId = b.dataset.id;
      return {
        id: productId,
        type: "add_to_order",
        sortKey: index + 1,
      };
    });
    try {
      await updateRecommendedProducts({
        recommendedProducts: (products as unknown) as RecommendedProduct[],
        organizationKeyName,
        menuId: id,
      });
      toastr.success("Success", "Products reordered successfully");
    } catch (error: unknown) {
      const message = getErrorMessage(error);
      toastr.error("Error", message);
    }
  };

  useHandleFetchError(getMenuRecommendedProductsError);

  if (isLoading || !data || isRefetching) {
    return <SpinnerBlock />;
  }

  return (
    <PageWrapper>
      <CardContainer>
        <PageHeader
          title="Recommended products"
          subtitle="If any products are added to this list, they will be recommended to customers when they go to checkout."
        >
          <AddRecommendedProductsSelector currentRecommendedProducts={data} />
        </PageHeader>
        <div>
          <Table>
            <thead>
              <TableRowHeader>
                <th />
                <TableCellHeaderText>Product name</TableCellHeaderText>
                <TableCellHeaderText>Price</TableCellHeaderText>
                <th />
              </TableRowHeader>
            </thead>
            <DragAndDropContainer
              isTable={true}
              onEnd={onDrop}
              containerStyle={dragAndDropContainerStyle}
            >
              {data
                .sort(
                  (a, b) =>
                    // Can be a string
                    Number(a.sortKey) - Number(b.sortKey)
                )
                .map((rp, i) => (
                  <TableRow
                    key={i}
                    data-id={rp.product.id}
                    css={dragAndDropRowStyle}
                  >
                    <TableCellText className="pl-5">
                      <StyledText
                        className="bodyTextRegular mb-0"
                        color={colors.textPrimary}
                      >
                        <FontAwesomeIcon icon={solid("grip-vertical")} />
                      </StyledText>
                    </TableCellText>
                    <TableCellText>
                      <div css={productNameContainerStyle}>
                        <Avatar
                          imageUrl={rp.product.imageUrl}
                          backgroundColor={colors.greyBorder}
                        >
                          {rp.product.name.charAt(0).toUpperCase()}
                        </Avatar>
                        <StyledText
                          className="bodyTextRegular mb-0"
                          color={colors.textPrimary}
                        >
                          {rp.product.name}
                        </StyledText>
                      </div>
                    </TableCellText>
                    <TableCellText>
                      <StyledText
                        className="bodyTextRegular mb-0"
                        color={colors.textPrimary}
                      >
                        {formatMoneyAmount(
                          rp.product.price.amount,
                          rp.product.price.currency
                        )}
                      </StyledText>
                    </TableCellText>
                    <td css={dropdownWrapperStyle}>
                      <div css={ellipsisWrapperStyle}>
                        <RecommendedProductDropdown
                          recommendedProduct={rp}
                          menuId={id}
                          recommendedProducts={data}
                        />
                      </div>
                    </td>
                  </TableRow>
                ))}
            </DragAndDropContainer>
          </Table>
          <PageFooter>
            <StyledText
              className="bodyTextRegular mb-0"
              color={colors.textPrimary}
            >
              {data?.length} recommended{" "}
              {data?.length === 1 ? "product" : "products"} in total
            </StyledText>
          </PageFooter>
        </div>
      </CardContainer>
    </PageWrapper>
  );
};
